/**
 * React Query hooks for the Workflow Control Plane.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAgents,
  getSessions,
  getSessionDetail,
  createSession,
  transitionSession,
  sendSessionMessage,
  getCronJobs,
  getWorkflowStatus,
  sendCommand,
  upsertConfig,
  getConfigs,
  deleteConfig,
} from "./workflowApi";
import type {
  WorkflowAgent,
  SessionDetailResponse,
  SessionsListResponse,
  WorkflowStatusResponse,
  CronJobsResponse,
  ConfigsListResponse,
  SessionStatus,
  SessionMessage,
} from "./types";

// Re-export useSessionSSE so callers can import from one place
export { useSessionSSE } from "./useSessionSSE";

// ── Query Keys ──────────────────────────────────────────────────────────

export const workflowKeys = {
  all: ["workflow"] as const,
  agents: () => [...workflowKeys.all, "agents"] as const,
  sessions: (params?: Record<string, unknown>) =>
    [...workflowKeys.all, "sessions", params] as const,
  sessionDetail: (sessionId: string) => [...workflowKeys.all, "session-detail", sessionId] as const,
  cronJobs: () => [...workflowKeys.all, "cron-jobs"] as const,
  status: () => [...workflowKeys.all, "status"] as const,
  configs: (namespace?: string) => [...workflowKeys.all, "configs", namespace] as const,
};

// ── Hooks ───────────────────────────────────────────────────────────────

/**
 * Fetch all workflow agents.
 */
export function useWorkflowAgents() {
  return useQuery({
    queryKey: workflowKeys.agents(),
    queryFn: async (): Promise<WorkflowAgent[]> => {
      const response = await getAgents();
      return response.agents;
    },
    refetchInterval: 30_000,
  });
}

/**
 * Fetch sessions with pagination and optional status filter.
 */
export function useWorkflowSessions(params?: {
  status_filter?: string;
  page?: number;
  page_size?: number;
}) {
  return useQuery({
    queryKey: workflowKeys.sessions(params),
    queryFn: async (): Promise<SessionsListResponse> => {
      return getSessions(params);
    },
    refetchInterval: 15_000,
  });
}

/**
 * Fetch a single session's detail including messages.
 */
export function useSessionDetail(sessionId: string | null) {
  return useQuery({
    queryKey: workflowKeys.sessionDetail(sessionId ?? ""),
    queryFn: async (): Promise<SessionDetailResponse> => {
      if (!sessionId) throw new Error("No session ID");
      return getSessionDetail(sessionId);
    },
    enabled: !!sessionId,
    refetchInterval: 30_000, // SSE handles real-time; polling is a fallback
  });
}

/**
 * Fetch cron jobs.
 */
export function useCronJobs() {
  return useQuery({
    queryKey: workflowKeys.cronJobs(),
    queryFn: async (): Promise<CronJobsResponse> => {
      return getCronJobs();
    },
    refetchInterval: 60_000,
  });
}

/**
 * Fetch overall workflow status (agent online/offline, vitals, LLM usage, capacity).
 */
export function useWorkflowStatus() {
  return useQuery({
    queryKey: workflowKeys.status(),
    queryFn: async (): Promise<WorkflowStatusResponse> => {
      return getWorkflowStatus();
    },
    refetchInterval: 30_000,
  });
}

/**
 * Mutation: send a message to a session from the dashboard.
 */
export function useSendMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, content }: { sessionId: string; content: string }) =>
      sendSessionMessage(sessionId, content),
    onMutate: async ({ sessionId, content }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: workflowKeys.sessionDetail(sessionId) });

      // Snapshot previous value
      const prev = queryClient.getQueryData<SessionDetailResponse>(
        workflowKeys.sessionDetail(sessionId)
      );

      // Optimistically add the user message
      if (prev) {
        const optimisticMsg: SessionMessage = {
          id: Date.now(), // temporary ID
          role: "user",
          content,
          created_at: new Date().toISOString(),
        };
        queryClient.setQueryData<SessionDetailResponse>(workflowKeys.sessionDetail(sessionId), {
          ...prev,
          messages: [...prev.messages, optimisticMsg],
        });
      }
      return { prev };
    },
    onError: (_err, variables, context) => {
      // Rollback on error
      if (context?.prev) {
        queryClient.setQueryData(workflowKeys.sessionDetail(variables.sessionId), context.prev);
      }
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.sessionDetail(variables.sessionId) });
    },
  });
}

/**
 * Mutation: create a new session from the dashboard.
 */
export function useCreateSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      title: string;
      description?: string;
      channel?: string;
      priority?: string;
      agent_type?: string;
    }) => createSession(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.all });
    },
  });
}

/**
 * Mutation: transition a session to a new status.
 */
export function useTransitionSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, status }: { sessionId: string; status: SessionStatus }) =>
      transitionSession(sessionId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.all });
    },
  });
}

/**
 * Mutation: update a session (assign agent, change priority, etc.).
 */
export function useUpdateSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, data }: { sessionId: string; data: Record<string, unknown> }) => {
      // Import inline to avoid circular dep
      return import("./workflowApi").then(mod => mod.updateSession(sessionId, data));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.all });
    },
  });
}

/**
 * Mutation: send a command to the agent (restart, config-reload).
 */
export function useSendCommand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      command,
      payload,
    }: {
      command: "restart" | "config-reload";
      payload?: Record<string, unknown>;
    }) => sendCommand(command, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.status() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.agents() });
    },
  });
}

/**
 * Fetch workflow configs, optionally filtered by namespace.
 */
export function useWorkflowConfigs(namespace?: string) {
  return useQuery({
    queryKey: workflowKeys.configs(namespace),
    queryFn: async (): Promise<ConfigsListResponse> => {
      return getConfigs(namespace);
    },
    refetchInterval: 30_000,
  });
}

/**
 * Mutation: upsert a config value.
 */
export function useUpdateConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { namespace: string; key: string; value: unknown; encrypted?: boolean }) =>
      upsertConfig(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.all });
    },
  });
}

/**
 * Mutation: delete a config entry.
 */
export function useDeleteConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (configId: string) => deleteConfig(configId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.all });
    },
  });
}

